import {
  Alert,
  Box,
  Button,
  CopyButton,
  FileButton,
  Group,
  Image,
  Modal,
  NumberInput,
  Select,
  SimpleGrid,
  Stack,
  Table,
  Text,
  TextInput,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconCheck, IconCopy, IconKey, IconPlus, IconTrash, IconUpload } from '@tabler/icons-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, izvedi, podatki, type Shema } from '../api/odjemalec';
import { NapakaApi, sporocilo } from '../api/napaka';
import { Kartica, StanjeNalaganja } from '../postavitev/Podatki';
import { barve } from '../tema';
import { PomocIkona } from '../pomoc/PomocIkona';
import { seja } from '../seja/seja';

type Uporabnik = Shema['UporabnikPodatki'];

const POLJA: [keyof Uporabnik, string][] = [
  ['uporabniskoIme', 'Uporabniško ime'],
  ['ime', 'Ime'],
  ['priimek', 'Priimek'],
  ['email', 'E-pošta'],
  ['davcnaStevilka', 'Davčna št.'],
  ['naslov', 'Naslov'],
  ['funkcija', 'Funkcija na dokumentih'],
];

const NAJVEC_MB = 5;

/**
 * Uporabniki podjetja – kot zavihek Uporabniki v Supermanu (UporabnikDetailsDialog): seznam z vlogo, dodajanje in
 * urejanje, ponastavitev gesla. Novi uporabnik in ponastavitev dobita naključno začasno geslo (strežnik), ki ga je treba
 * ob prvi prijavi zamenjati. Vloga določa pravice (Nastavitve → Vloge in pravice); zadnjega skrbnika strežnik ne pusti
 * spremeniti.
 */
export function Uporabniki() {
  const odjemalec = useQueryClient();
  const seznam = useQuery({
    queryKey: ['uporabniki'],
    queryFn: ({ signal }) => podatki(api.GET('/api/uporabniki', { signal })),
  });
  const vloge = useQuery({
    queryKey: ['vloge'],
    queryFn: ({ signal }) => podatki(api.GET('/api/vloge', { signal })),
  });
  const [u, setU] = useState<Uporabnik | null>(null);
  const [polja, setPolja] = useState<Record<string, string>>({});
  const [napaka, setNapaka] = useState<string | null>(null);
  const [dela, setDela] = useState<string | null>(null);
  const [geslo, setGeslo] = useState<{ ime: string; geslo: string } | null>(null);
  const [ponastavi, setPonastavi] = useState(false);

  if (!seznam.data) {
    return (
      <StanjeNalaganja
        napaka={seznam.error}
        ponovi={() => void seznam.refetch()}
        naslov="Uporabnikov ni bilo mogoče naložiti"
      />
    );
  }

  const izvedba = async (kdo: string, dejanje: () => Promise<void>) => {
    setNapaka(null);
    setPolja({});
    setDela(kdo);
    try {
      await dejanje();
      await odjemalec.invalidateQueries({ queryKey: ['uporabniki'] });
      await odjemalec.invalidateQueries({ queryKey: ['vloge'] });
      // morda je spremenjena vloga prijavljenega uporabnika
      void seja.osveziPravice();
    } catch (e) {
      if (e instanceof NapakaApi && Object.keys(e.polja).length > 0) {
        setPolja(e.polja);
      } else {
        setNapaka(sporocilo(e));
      }
    } finally {
      setDela(null);
    }
  };

  const shrani = () =>
    izvedba('shrani', async () => {
      if (!u) {
        return;
      }
      if (u.prijavaId) {
        setU(await podatki(api.PUT('/api/uporabniki/{id}', { params: { path: { id: u.prijavaId } }, body: u })));
        notifications.show({ color: 'green', message: 'Uporabnik je shranjen.' });
      } else {
        const z = await podatki(api.POST('/api/uporabniki', { body: u }));
        setGeslo({ ime: u.uporabniskoIme ?? '', geslo: z.zacasnoGeslo ?? '' });
        setU(null);
      }
    });

  const ponastaviGeslo = () =>
    izvedba('geslo', async () => {
      const z = await podatki(
        api.POST('/api/uporabniki/{id}/ponastavi-geslo', { params: { path: { id: u!.prijavaId! } } }),
      );
      setPonastavi(false);
      setGeslo({ ime: u?.uporabniskoIme ?? '', geslo: z.zacasnoGeslo ?? '' });
    });

  return (
    <Stack gap="md">
      <Kartica
        naslov={`Uporabniki (${seznam.data.length})`}
        dejanja={
          <Button size="xs" variant="light" leftSection={<IconPlus size={14} />} onClick={() => setU({ casSeje: 60 })}>
            Nov uporabnik
          </Button>
        }
      >
        <Table.ScrollContainer minWidth={520}>
          <Table highlightOnHover verticalSpacing={6}>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Uporabniško ime</Table.Th>
                <Table.Th>Ime in priimek</Table.Th>
                <Table.Th>Vloga</Table.Th>
                <Table.Th>E-pošta</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {seznam.data.map((x) => (
                <Table.Tr
                  key={x.prijavaId}
                  onClick={() => {
                    setU(x);
                    setPolja({});
                    setNapaka(null);
                  }}
                  style={{ cursor: 'pointer', background: x.prijavaId === u?.prijavaId ? barve.izbrano : undefined }}
                >
                  <Table.Td fw={600}>{x.uporabniskoIme}</Table.Td>
                  <Table.Td>{[x.ime, x.priimek].filter(Boolean).join(' ')}</Table.Td>
                  <Table.Td>{x.vloga}</Table.Td>
                  <Table.Td>{x.email}</Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      </Kartica>
      {u && (
        <Kartica naslov={u.prijavaId ? `Uporabnik ${u.uporabniskoIme ?? ''}` : 'Nov uporabnik'}>
          <SimpleGrid cols={{ base: 1, xs: 2 }} spacing="sm">
            <Select
              label="Vloga"
              withAsterisk
              placeholder="Izberite vlogo"
              data={(vloge.data?.vloge ?? []).map((x) => ({ value: String(x.id), label: x.naziv ?? '' }))}
              value={u.vlogaId ? String(u.vlogaId) : null}
              onChange={(v) => setU({ ...u, vlogaId: v ? Number(v) : undefined })}
              error={polja.vlogaId ?? (vloge.error ? sporocilo(vloge.error) : undefined)}
              description="Kaj uporabnik sme videti in spreminjati."
              allowDeselect={false}
            />
            {POLJA.map(([k, oznaka]) => (
              <TextInput
                key={k}
                label={oznaka}
                value={String(u[k] ?? '')}
                onChange={(e) => setU({ ...u, [k]: e.currentTarget.value })}
                error={polja[k]}
                withAsterisk={k === 'uporabniskoIme'}
                placeholder={k === 'funkcija' ? 'npr. prokurist' : undefined}
                description={
                  k === 'funkcija' ? 'Na računu in predračunu: »Izdal: ime in priimek, funkcija«.' : undefined
                }
              />
            ))}
            <NumberInput
              label="Čas seje (minute)"
              value={u.casSeje ?? ''}
              onChange={(v) => setU({ ...u, casSeje: typeof v === 'number' ? v : undefined })}
              min={1}
              allowDecimal={false}
              error={polja.casSeje}
            />
          </SimpleGrid>
          {!u.prijavaId && (
            <Text size="xs" c="dimmed" mt="sm">
              Uporabnik dobi začasno geslo, ki ga ob prvi prijavi zamenja.
            </Text>
          )}
          {napaka && (
            <Alert color="red" variant="light" mt="sm" role="alert">
              {napaka}
            </Alert>
          )}
          <Group justify="space-between" mt="md" gap="xs">
            <Group gap="xs">
              {u.prijavaId && (
                <Button variant="default" leftSection={<IconKey size={18} />} onClick={() => setPonastavi(true)}>
                  Ponastavi geslo
                </Button>
              )}
            </Group>
            <Group gap="xs">
              <Button variant="subtle" onClick={() => setU(null)}>
                Prekliči
              </Button>
              <Button onClick={() => void shrani()} loading={dela === 'shrani'}>
                {u.prijavaId ? 'Shrani' : 'Dodaj'}
              </Button>
            </Group>
          </Group>
        </Kartica>
      )}
      {u?.prijavaId && <PodpisUporabnika key={u.prijavaId} prijavaId={u.prijavaId} />}
      <Modal opened={ponastavi} onClose={() => setPonastavi(false)} title="Ponastavi geslo" radius="lg">
        <Text size="sm">
          Uporabnik {u?.uporabniskoIme} dobi novo začasno geslo; staro geslo ne bo več veljalo. Želite nadaljevati?
        </Text>
        <Group justify="flex-end" mt="md" gap="xs">
          <Button variant="subtle" onClick={() => setPonastavi(false)}>
            Prekliči
          </Button>
          <Button onClick={() => void ponastaviGeslo()} loading={dela === 'geslo'}>
            Ponastavi
          </Button>
        </Group>
      </Modal>
      <Modal opened={geslo !== null} onClose={() => setGeslo(null)} title="Začasno geslo" radius="lg">
        <Stack gap="sm">
          <Text size="sm">
            Začasno geslo za <b>{geslo?.ime}</b> je prikazano samo enkrat. Sporočite ga uporabniku; ob prvi prijavi ga
            bo moral zamenjati.
          </Text>
          <Group gap="xs" wrap="nowrap">
            <TextInput value={geslo?.geslo ?? ''} readOnly style={{ flex: 1 }} aria-label="Začasno geslo" />
            <CopyButton value={geslo?.geslo ?? ''}>
              {({ copied, copy }) => (
                <Button
                  variant="light"
                  onClick={copy}
                  leftSection={copied ? <IconCheck size={16} /> : <IconCopy size={16} />}
                >
                  {copied ? 'Kopirano' : 'Kopiraj'}
                </Button>
              )}
            </CopyButton>
          </Group>
          <Group justify="flex-end">
            <Button onClick={() => setGeslo(null)}>Zapri</Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}

/** Vsebina datoteke kot Base64 (brez »data:…;base64,«) za PodpisPodatki.slika. */
function base64(datoteka: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result).replace(/^data:[^,]*,/, ''));
    r.onerror = () => reject(new NapakaApi('Datoteke ni bilo mogoče prebrati.', 'DATOTEKA', 0));
    r.readAsDataURL(datoteka);
  });
}

/**
 * Podpis uporabnika za račun in predračun (nastavitev »Natisni podpis izdajatelja« v skupini Dokumenti): prikaz,
 * nalaganje JPG/PNG in odstranitev. Strežnik obreže bel rob in sliko pomanjša.
 */
function PodpisUporabnika({ prijavaId }: { prijavaId: number }) {
  const odjemalec = useQueryClient();
  const kljuc = ['podpis', prijavaId];
  const podpis = useQuery({
    queryKey: kljuc,
    // 204 = uporabnik podpisa nima
    queryFn: async ({ signal }) =>
      (await podatki(api.GET('/api/uporabniki/{id}/podpis', { signal, params: { path: { id: prijavaId } } }))) ?? null,
  });
  const [napaka, setNapaka] = useState<string | null>(null);
  const [dela, setDela] = useState<string | null>(null);

  const izvedba = async (kdo: string, dejanje: () => Promise<unknown>, sporociloUspeha: string) => {
    setNapaka(null);
    setDela(kdo);
    try {
      await dejanje();
      await odjemalec.invalidateQueries({ queryKey: kljuc });
      await odjemalec.invalidateQueries({ queryKey: ['uporabniki'] });
      notifications.show({ color: 'green', message: sporociloUspeha });
    } catch (e) {
      setNapaka(e instanceof NapakaApi && e.polja.podpis ? e.polja.podpis : sporocilo(e));
    } finally {
      setDela(null);
    }
  };

  const nalozi = async (datoteka: File | null) => {
    if (!datoteka) {
      return;
    }
    if (!/^image\/(png|jpe?g)$/.test(datoteka.type)) {
      setNapaka('Izberite sliko JPG ali PNG.');
      return;
    }
    if (datoteka.size > NAJVEC_MB * 1024 * 1024) {
      setNapaka(`Slika je prevelika (največ ${NAJVEC_MB} MB).`);
      return;
    }
    const slika = await base64(datoteka);
    await izvedba(
      'nalozi',
      () => podatki(api.PUT('/api/uporabniki/{id}/podpis', { params: { path: { id: prijavaId } }, body: { slika } })),
      'Podpis je naložen.',
    );
  };

  const slika = podpis.data?.slika;
  return (
    <Kartica
      naslov="Podpis na dokumentih"
      dejanja={
        <Group gap="xs">
          <PomocIkona clanek="podpis-na-dokumentih" />
          <FileButton onChange={(f) => void nalozi(f)} accept="image/png,image/jpeg">
            {(p) => (
              <Button
                {...p}
                size="xs"
                variant="light"
                leftSection={<IconUpload size={14} />}
                loading={dela === 'nalozi'}
              >
                {slika ? 'Zamenjaj' : 'Naloži podpis'}
              </Button>
            )}
          </FileButton>
          {slika && (
            <Button
              size="xs"
              variant="subtle"
              color="red"
              leftSection={<IconTrash size={14} />}
              loading={dela === 'odstrani'}
              onClick={() =>
                void izvedba(
                  'odstrani',
                  () => izvedi(api.DELETE('/api/uporabniki/{id}/podpis', { params: { path: { id: prijavaId } } })),
                  'Podpis je odstranjen.',
                )
              }
            >
              Odstrani
            </Button>
          )}
        </Group>
      }
    >
      <Stack gap="sm">
        {slika ? (
          <Box p="md" style={{ border: `1px solid ${barve.obroba}`, borderRadius: 10, background: 'white' }}>
            <Image src={`data:image/png;base64,${slika}`} alt="Podpis uporabnika" fit="contain" mah={120} />
          </Box>
        ) : (
          <Text size="sm" c="dimmed">
            {podpis.isPending ? 'Nalagam …' : 'Uporabnik še nima naloženega podpisa.'}
          </Text>
        )}
        <Text size="xs" c="dimmed">
          Fotografija ali sken podpisa na belem papirju (JPG ali PNG). Natisne se nad črto prodajalca na računih in
          predračunih, ki jih izda ta uporabnik, ko je v skupini Dokumenti vklopljeno »Natisni podpis izdajatelja«.
        </Text>
        {napaka && (
          <Alert color="red" variant="light" role="alert">
            {napaka}
          </Alert>
        )}
        {podpis.error && !napaka && (
          <Alert color="red" variant="light" role="alert">
            {sporocilo(podpis.error)}
          </Alert>
        )}
      </Stack>
    </Kartica>
  );
}
